import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';

const C = {
  teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981',
  dark:'#1F2937', grey:'#6B7280', bg:'#F0F7FA', border:'#E5E7EB',
  red:'#EF4444', gold:'#F59E0B',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short', year:'numeric' });
}

function StatusBadge({ status }) {
  if (status === 'cancelled') return (
    <span style={{ background:'#FEE2E2', color:'#DC2626', fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:20 }}>Cancelled</span>
  );
  if (status === 'completed') return (
    <span style={{ background:'#D1FAE5', color:'#065F46', fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:20 }}>✓ Completed</span>
  );
  return (
    <span style={{ background:'#FEF3C7', color:'#92400E', fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:20 }}>{status || 'Booked'}</span>
  );
}

function AttendanceBadge({ mode }) {
  if (!mode || mode === 'in_person') return (
    <span style={{ background:'#F0FDF4', color:'#065F46', fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20 }}>🏫 In Person</span>
  );
  if (mode === 'remote') return (
    <span style={{ background:'#EFF6FF', color:'#1D4ED8', fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20 }}>📹 Remote</span>
  );
  if (mode === 'absent') return (
    <span style={{ background:'#FEE2E2', color:'#DC2626', fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20 }}>❌ Absent</span>
  );
  return null;
}

function StarPicker({ value, onChange, size = 28 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display:'flex', gap:4 }}>
      {[1,2,3,4,5].map(s => (
        <span
          key={s}
          onMouseEnter={() => setHovered(s)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(s)}
          style={{
            fontSize: size,
            cursor: 'pointer',
            color: s <= (hovered || value) ? C.gold : '#D1D5DB',
            transition: 'color 0.1s',
            userSelect: 'none',
          }}>★</span>
      ))}
    </div>
  );
}

function StarDisplay({ stars, size = 14 }) {
  return (
    <span style={{ display:'inline-flex', gap:2 }}>
      {[1,2,3,4,5].map(s => (
        <span key={s} style={{ fontSize:size, color: s <= stars ? C.gold : '#D1D5DB' }}>★</span>
      ))}
    </span>
  );
}

function ReviewModal({ session, onClose, onSubmitted }) {
  const [stars,       setStars]       = useState(0);
  const [comment,     setComment]     = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [done,        setDone]        = useState(false);

  const submit = async () => {
    if (stars === 0) return;
    setSubmitting(true);
    try {
      await api.post(`/api/student/bookings/${session.id}/review`, { stars, comment });
      setDone(true);
      setTimeout(() => { onSubmitted(session.id, stars); onClose(); }, 1400);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not submit review');
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:440, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        {done ? (
          <div style={{ textAlign:'center', padding:'16px 0' }}>
            <div style={{ fontSize:52, marginBottom:12 }}>🎉</div>
            <div style={{ fontWeight:800, fontSize:20, color:C.navy, marginBottom:6 }}>Thank you!</div>
            <div style={{ color:C.grey, fontSize:14 }}>Your review has been submitted.</div>
          </div>
        ) : (
          <>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20 }}>
              <div>
                <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:C.navy }}>Rate your session</h3>
                <div style={{ color:C.grey, fontSize:13, marginTop:3 }}>
                  with <strong>{session.tutor_name}</strong> · {formatDate(session.date)}
                </div>
              </div>
              <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:C.grey, lineHeight:1, padding:4 }}>✕</button>
            </div>

            <div style={{ marginBottom:20, textAlign:'center' }}>
              <div style={{ fontSize:13, color:C.grey, marginBottom:10, fontWeight:600 }}>How was your session?</div>
              <div style={{ display:'flex', justifyContent:'center' }}>
                <StarPicker value={stars} onChange={setStars} size={40} />
              </div>
              {stars > 0 && (
                <div style={{ marginTop:8, fontSize:13, color:C.gold, fontWeight:700 }}>
                  {['','Poor','Below Average','Good','Very Good','Excellent!'][stars]}
                </div>
              )}
            </div>

            <div style={{ marginBottom:22 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:C.dark, marginBottom:6 }}>
                Leave a comment <span style={{ color:C.grey, fontWeight:400 }}>(optional)</span>
              </label>
              <textarea
                rows={3}
                placeholder="What did you enjoy? What could be improved?"
                value={comment}
                onChange={e => setComment(e.target.value)}
                maxLength={1000}
                style={{ width:'100%', border:`1.5px solid ${C.border}`, borderRadius:10, padding:'10px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box', fontFamily:'inherit', outline:'none' }}
              />
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={onClose} style={{ flex:1, padding:'11px', border:`1.5px solid ${C.border}`, borderRadius:10, background:'#fff', color:C.grey, fontWeight:600, fontSize:13, cursor:'pointer' }}>
                Maybe later
              </button>
              <button onClick={submit} disabled={stars === 0 || submitting} style={{
                flex:2, padding:'11px', background: stars > 0 ? C.gold : '#E5E7EB',
                color: stars > 0 ? '#fff' : C.grey, border:'none', borderRadius:10,
                fontWeight:700, fontSize:13, cursor: stars > 0 ? 'pointer' : 'default',
                transition:'all 0.15s',
              }}>
                {submitting ? 'Submitting…' : '★ Submit Review'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function StudentSessions() {
  const navigate = useNavigate();
  const [sessions,    setSessions]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [expanded,    setExpanded]    = useState(null);
  const [filter,      setFilter]      = useState('all');
  const [reviewModal, setReviewModal] = useState(null); // session object

  useEffect(() => {
    api.get('/api/student/session-history')
      .then(res => setSessions(res.data?.sessions || []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, []);

  const handleReviewSubmitted = (bookingId, stars) => {
    setSessions(prev => prev.map(s =>
      s.id === bookingId ? { ...s, has_review: true, review_stars: stars } : s
    ));
  };

  const today = new Date().toISOString().split('T')[0];

  const filtered = sessions.filter(s => {
    if (filter === 'completed') return s.status === 'completed' || s.date < today;
    if (filter === 'upcoming')  return s.date >= today && s.status !== 'cancelled';
    return true;
  });

  const completedCount = sessions.filter(s => s.status === 'completed' || (s.date < today && s.status !== 'cancelled')).length;
  const withNotesCount = sessions.filter(s => s.has_notes).length;
  const pendingReviews = sessions.filter(s => s.status === 'completed' && !s.has_review).length;

  return (
    <div style={{ minHeight:'100vh', background:'#F8FAFC', fontFamily:'Inter,system-ui,sans-serif' }}>
      {/* Header */}
      <header style={{ background:C.navy, color:'#fff', padding:'0 20px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ fontWeight:800, fontSize:20 }}>SpeakIQ</div>
        <nav style={{ display:'flex', gap:20 }}>
          {[['Dashboard','/dashboard'],['My Sessions','/my-sessions'],['Homework','/homework'],['Vocabulary','/vocabulary'],['My ID','/my-id']].map(([label, path]) => (
            <Link key={path} to={path} style={{ color: path === '/my-sessions' ? '#00B4D8' : 'rgba(255,255,255,0.8)', textDecoration:'none', fontSize:13, fontWeight: path === '/my-sessions' ? 700 : 500 }}>{label}</Link>
          ))}
        </nav>
        <button onClick={() => { localStorage.clear(); navigate('/login'); }} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:13 }}>
          Logout
        </button>
      </header>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'32px 20px' }}>
        {/* Page title */}
        <div style={{ marginBottom:24 }}>
          <h1 style={{ margin:0, fontSize:24, fontWeight:800, color:C.navy }}>My Sessions</h1>
          <p style={{ margin:'4px 0 0', color:C.grey, fontSize:14 }}>Your complete session history with lesson notes and feedback</p>
        </div>

        {/* Stats strip */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
          {[
            { label:'Total Sessions', value: sessions.length,  icon:'📅' },
            { label:'Completed',      value: completedCount,   icon:'✅' },
            { label:'With Notes',     value: withNotesCount,   icon:'📝' },
            { label:'Reviews Pending', value: pendingReviews,  icon:'⭐', highlight: pendingReviews > 0 },
          ].map(stat => (
            <div key={stat.label} style={{
              background: stat.highlight ? '#FFFBEB' : '#fff',
              borderRadius:12, padding:'16px 20px',
              border: stat.highlight ? `1.5px solid #FDE68A` : `1.5px solid ${C.border}`,
              display:'flex', alignItems:'center', gap:12,
            }}>
              <span style={{ fontSize:24 }}>{stat.icon}</span>
              <div>
                <div style={{ fontWeight:800, fontSize:22, color: stat.highlight ? C.gold : C.navy }}>{stat.value}</div>
                <div style={{ fontSize:12, color:C.grey }}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Pending review prompt banner */}
        {pendingReviews > 0 && (
          <div style={{ background:'linear-gradient(135deg,#FFFBEB,#FEF3C7)', border:'1.5px solid #FDE68A', borderRadius:14, padding:'14px 20px', marginBottom:20, display:'flex', alignItems:'center', gap:14 }}>
            <span style={{ fontSize:28 }}>⭐</span>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:14, color:'#78350F' }}>You have {pendingReviews} session{pendingReviews>1?'s':''} to review</div>
              <div style={{ fontSize:13, color:'#92400E', marginTop:2 }}>Your feedback helps improve your tutor's teaching and the school.</div>
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          {[['all','All'],['upcoming','Upcoming'],['completed','Completed']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} style={{
              padding:'6px 16px', borderRadius:20, fontSize:13, fontWeight:600, cursor:'pointer',
              background: filter === val ? C.teal : '#fff',
              color: filter === val ? '#fff' : C.grey,
              border: filter === val ? `1.5px solid ${C.teal}` : `1.5px solid ${C.border}`,
            }}>{label}</button>
          ))}
        </div>

        {/* Session list */}
        {loading ? (
          <div style={{ textAlign:'center', padding:60, color:C.grey }}>Loading your sessions…</div>
        ) : filtered.length === 0 ? (
          <div style={{ background:'#fff', borderRadius:14, border:`1.5px solid ${C.border}`, padding:48, textAlign:'center' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
            <div style={{ fontWeight:700, color:C.dark, marginBottom:6 }}>No sessions found</div>
            <div style={{ color:C.grey, fontSize:14 }}>
              {filter === 'upcoming' ? 'No upcoming sessions. Book one from the dashboard.' : 'Your session history will appear here after your first lesson.'}
            </div>
            {filter === 'upcoming' && (
              <button onClick={() => navigate('/book-tutor')} style={{ marginTop:16, padding:'10px 24px', background:C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
                Book a Class
              </button>
            )}
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filtered.map(session => {
              const isOpen   = expanded === session.id;
              const isPast   = session.date < today || session.status === 'completed';
              const canReview = session.status === 'completed' && !session.has_review;
              return (
                <div key={session.id} style={{ background:'#fff', borderRadius:14, border:`1.5px solid ${C.border}`, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                  {/* Summary row */}
                  <div
                    onClick={() => isPast && setExpanded(isOpen ? null : session.id)}
                    style={{ padding:'14px 20px', display:'flex', alignItems:'center', gap:12, cursor: isPast ? 'pointer' : 'default', flexWrap:'wrap' }}>
                    {/* Tutor avatar */}
                    <div style={{ width:40, height:40, borderRadius:'50%', overflow:'hidden', flexShrink:0, background:'#E5E7EB', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {session.tutor_photo
                        ? <img src={session.tutor_photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                        : <span style={{ fontSize:18 }}>👩‍🏫</span>}
                    </div>

                    {/* Date + tutor */}
                    <div style={{ flex:1, minWidth:160 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:C.dark }}>{session.tutor_name}</div>
                      <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>
                        {formatDate(session.date)} · {session.time} · {session.duration_minutes || 60} min
                      </div>
                    </div>

                    {/* Badges */}
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                      <StatusBadge status={session.status} />
                      <AttendanceBadge mode={session.attendance_mode} />
                      {session.has_notes && (
                        <span style={{ background:'#F3E8FF', color:'#7C3AED', fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20 }}>📝 Notes</span>
                      )}
                      {session.has_review && (
                        <span style={{ background:'#FFFBEB', color:'#92400E', fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:20, display:'flex', alignItems:'center', gap:3 }}>
                          <StarDisplay stars={session.review_stars} size={12} /> Reviewed
                        </span>
                      )}
                    </div>

                    {/* Review button or expand chevron */}
                    <div style={{ display:'flex', alignItems:'center', gap:8 }} onClick={e => e.stopPropagation()}>
                      {canReview && (
                        <button
                          onClick={() => setReviewModal(session)}
                          style={{ padding:'6px 14px', background:'linear-gradient(135deg,#F59E0B,#D97706)', color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer', whiteSpace:'nowrap', boxShadow:'0 2px 8px rgba(245,158,11,0.3)' }}>
                          ⭐ Rate Session
                        </button>
                      )}
                      {isPast && <span style={{ color:C.grey, fontSize:16, transition:'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none', display:'inline-block', cursor:'pointer' }} onClick={() => setExpanded(isOpen ? null : session.id)}>▾</span>}
                    </div>
                  </div>

                  {/* Expanded notes panel */}
                  {isOpen && isPast && (
                    <div style={{ borderTop:`1px solid ${C.border}`, padding:'16px 20px', background:'#FAFAFA' }}>
                      {session.lesson_notes ? (
                        <>
                          <div style={{ marginBottom:14 }}>
                            <div style={{ fontWeight:700, fontSize:12, color:C.dark, marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
                              📚 <span>What We Covered</span>
                            </div>
                            <div style={{ background:'#fff', borderRadius:10, padding:'12px 16px', border:`1px solid ${C.border}`, fontSize:13, color:C.dark, lineHeight:1.7, whiteSpace:'pre-wrap' }}>
                              {session.lesson_notes}
                            </div>
                          </div>

                          {session.homework_notes && (
                            <div style={{ marginBottom:14 }}>
                              <div style={{ fontWeight:700, fontSize:12, color:C.dark, marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
                                ✏️ <span>Homework Assigned</span>
                              </div>
                              <div style={{ background:'#FFFBEB', borderRadius:10, padding:'12px 16px', border:'1px solid #FDE68A', fontSize:13, color:'#78350F', lineHeight:1.7, whiteSpace:'pre-wrap' }}>
                                {session.homework_notes}
                              </div>
                            </div>
                          )}

                          {session.private_note && (
                            <div style={{ marginBottom:14 }}>
                              <div style={{ fontWeight:700, fontSize:12, color:C.dark, marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
                                🔒 <span>Personal Feedback from Your Tutor</span>
                              </div>
                              <div style={{ background:'#F5F3FF', borderRadius:10, padding:'12px 16px', border:'1px solid #DDD6FE', fontSize:13, color:'#5B21B6', lineHeight:1.7, whiteSpace:'pre-wrap' }}>
                                {session.private_note}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ textAlign:'center', padding:'16px 0', color:C.grey, fontSize:13 }}>
                          No notes were recorded for this session yet.
                        </div>
                      )}

                      {/* Inline rate prompt if not reviewed */}
                      {canReview && (
                        <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                          <div style={{ fontSize:13, color:C.grey }}>How was this session? Share your feedback with your tutor.</div>
                          <button
                            onClick={() => setReviewModal(session)}
                            style={{ padding:'8px 18px', background:C.gold, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
                            ⭐ Leave a Review
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Review modal */}
      {reviewModal && (
        <ReviewModal
          session={reviewModal}
          onClose={() => setReviewModal(null)}
          onSubmitted={handleReviewSubmitted}
        />
      )}
    </div>
  );
}
